import { useLocalization } from '@lib/localization';
import {
  Box,
  Button,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type IFolder,
  type ShareAccessLevel,
  useAddFolderShareMutation,
  useRemoveFolderShareMutation,
  useUpdateFolderShareMutation,
} from '@store/api/folderEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import {
  buttonStyles,
  dangerHoverStyles,
  dialogCloseStyles,
  dialogFooterStyles,
  dialogHeaderStyles,
  dialogPaperStyles,
  memberRowStyles,
  menuItemStyles,
  selectStyles,
} from '@pages/Teams.styles';
import { useMemo, useState } from 'react';
import { TimesIcon, TrashIcon } from 'react-line-awesome';
import { SHARE_LEVEL_OPTIONS } from './shareLevels';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  folder: IFolder | null;
}

const ShareDialog = ({ open, onClose, folder }: ShareDialogProps) => {
  const { t } = useLocalization();

  const { data: directory = [] } = useGetTeamDirectoryQuery(undefined, { skip: !open });
  const [addShare, { isLoading: isAdding }] = useAddFolderShareMutation();
  const [updateShare, { isLoading: isUpdating }] = useUpdateFolderShareMutation();
  const [removeShare, { isLoading: isRemoving }] = useRemoveFolderShareMutation();
  const isSaving = isAdding || isUpdating || isRemoving;

  const ownerTeamId = folder?.ownerTeamId;

  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<ShareAccessLevel>('VIEW');

  const teamNameById = useMemo(() => new Map(directory.map((team) => [team._id, team.name])), [directory]);

  const shares = useMemo(() => folder?.sharedWith ?? [], [folder]);

  const candidateTeams = useMemo(() => {
    const sharedTeamIds = new Set(shares.map((share) => share.teamId));
    return directory.filter((team) => team._id !== ownerTeamId && !sharedTeamIds.has(team._id));
  }, [directory, shares, ownerTeamId]);

  const levelLabel = (level: ShareAccessLevel) => {
    const option = SHARE_LEVEL_OPTIONS.find((o) => o.value === level);
    return option ? t(option.labelKey) : level;
  };

  const handleClose = () => {
    if (isSaving) return;
    setSelectedTeamId('');
    setSelectedLevel('VIEW');
    onClose();
  };

  const handleAdd = async () => {
    if (!folder || !selectedTeamId) return;
    const result = await addShare({ folderId: folder._id, teamId: selectedTeamId, accessLevel: selectedLevel });
    if (!result.error) setSelectedTeamId('');
  };

  const handleLevelChange = (teamId: string, accessLevel: ShareAccessLevel) => {
    if (!folder) return;
    void updateShare({ folderId: folder._id, teamId, accessLevel });
  };

  const handleRevoke = (teamId: string) => {
    if (!folder) return;
    void removeShare({ folderId: folder._id, teamId });
  };

  const levelSelectItems = SHARE_LEVEL_OPTIONS.map((option) => (
    <MenuItem key={option.value} value={option.value} sx={menuItemStyles}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{t(option.labelKey)}</Typography>
        <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4 }}>
          {t(option.descriptionKey)}
        </Typography>
      </Box>
    </MenuItem>
  ));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 530 } } }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>{t('Folder_Share_Title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('Folder_Share_Subtitle')} · {folder?.path}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={dialogCloseStyles}>
          <TimesIcon />
        </IconButton>
      </Stack>

      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {shares.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center', fontSize: 14 }}>
            {t('Folder_Share_Empty')}
          </Typography>
        )}

        {shares.map((share) => (
          <Stack
            key={share.teamId}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={memberRowStyles}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 600, fontSize: 14.5 }}>
                {teamNameById.get(share.teamId) ?? share.teamId}
              </Typography>
              {share.sharedAt && (
                <Typography noWrap color="text.secondary" sx={{ fontSize: 13 }}>
                  {new Date(share.sharedAt).toAgoString()}
                </Typography>
              )}
            </Box>

            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <FormControl size="small">
                <Select<ShareAccessLevel>
                  value={share.accessLevel}
                  onChange={(e) => handleLevelChange(share.teamId, e.target.value as ShareAccessLevel)}
                  renderValue={(value) => levelLabel(value)}
                  disabled={isSaving}
                  sx={selectStyles}
                >
                  {levelSelectItems}
                </Select>
              </FormControl>
              <Tooltip title={t('Folder_Share_Revoke')} arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleRevoke(share.teamId)}
                    sx={dangerHoverStyles}
                    disabled={isSaving}
                  >
                    <TrashIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        ))}
      </Box>

      <Divider />

      <Stack direction="row" alignItems="center" spacing={1} sx={dialogFooterStyles}>
        <FormControl size="small" sx={{ flexGrow: 1 }}>
          <Select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            displayEmpty
            renderValue={(value) =>
              value ? teamNameById.get(value) ?? value : t('Folder_Share_SelectTeam')}
            disabled={isSaving}
            sx={selectStyles}
          >
            {candidateTeams.length === 0 && (
              <MenuItem value="" disabled sx={menuItemStyles}>
                {t('Folder_Share_NoTeams')}
              </MenuItem>
            )}
            {candidateTeams.map((team) => (
              <MenuItem key={team._id} value={team._id} sx={menuItemStyles}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select<ShareAccessLevel>
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value as ShareAccessLevel)}
            renderValue={(value) => levelLabel(value)}
            disabled={isSaving}
            sx={selectStyles}
          >
            {levelSelectItems}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={handleAdd}
          sx={buttonStyles}
          disabled={isSaving || !selectedTeamId}
        >
          {t('Folder_Share')}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default ShareDialog;
